import React from 'react';
import PropTypes from 'prop-types';
import throttle from 'lodash/throttle';

import { getWindowObject } from '@eb/feature-detection';
import { setWindowLocation } from '@eb/http';
import BaseGlobalHeader from 'eventbrite_design_system/globalHeader/GlobalHeader';

import {
    getHelpMenu,
    getOrganizeMenu,
    getLogoInfo,
    getSearchInfo,
    getBrowseEventsQuickLinks,
    getUserMenuInfo,
    getNonUserQuickLinks,
    getCreateEventCTA,
    isHomepage,
} from './utils';
import {
    ENV_SHAPE_PROP_TYPE,
    REQUEST_SHAPE_PROP_TYPE,
    USER_SHAPE_PROP_TYPE,
    FALLBACK_SUGGESTIONS,
} from './constants';
import { getDestinationSuggestions } from './utils/search';
import { getUserLocationFromRequest } from './utils/userLocation';

const SEARCH_SUGGESTIONS_THROTTLE_AMOUNT = 300;
const getDestinstionSuggestionsThrottled = throttle(
    getDestinationSuggestions,
    SEARCH_SUGGESTIONS_THROTTLE_AMOUNT,
);

export default class SiteHeader extends React.Component {
    static propTypes = {
        env: ENV_SHAPE_PROP_TYPE.isRequired,
        request: REQUEST_SHAPE_PROP_TYPE.isRequired,
        user: USER_SHAPE_PROP_TYPE.isRequired,
        onSearchSelect: PropTypes.func,
        hideSearchBar: PropTypes.bool,
        onSearchFocus: PropTypes.func,
    };

    constructor(props) {
        super(props);

        this.state = {
            searchQuery: props.env.searchQuery || '',
            searchSuggestions: FALLBACK_SUGGESTIONS,
        };
    }

    componentDidMount() {
        const LUX = getWindowObject('LUX');

        if (LUX) {
            LUX.mark('ch-search-ready');
        }

        this._setUserLocationOnState();
    }

    _setUserLocationOnState = () => {
        const {
            env: { searchLocation },
        } = this.props;

        this.setState({
            userLocation: getUserLocationFromRequest(searchLocation),
        });
    };

    _handleSearchChange = (searchQuery) => {
        if (!this.props.onSearchFocus) {
            getDestinstionSuggestionsThrottled(
                searchQuery,
                this.state.userLocation,
            ).then((searchSuggestions) => {
                this.setState({ searchQuery, searchSuggestions });
            });
        }
    };

    _handleSearchSubmit = (query) => {
        const LUX = getWindowObject('LUX');

        if (LUX) {
            LUX.mark('ch-search-sent');
        }
        if (!query) {
            setWindowLocation(`/d/local/all-events`);
        } else {
            setWindowLocation(`/d/?q=${query}`);
        }
    };

    render() {
        const {
            env,
            request,
            user,
            onSearchSelect,
            hideSearchBar,
            onSearchFocus,
            featureFlags,
        } = this.props;
        const { searchQuery, searchSuggestions } = this.state;
        const {
            serverUrl,
            adminServerUrl,
            loginUrl,
            logoutUrl,
            helpUrls,
            organizeUrls,
            ebDomain,
            isManageOverviewActive,
        } = env;
        const { path: requestPath } = request;
        const { isAuthenticated, canCreateEvents, isSuperCreator } = user;
        let additionalMenus = [
            getHelpMenu(helpUrls, requestPath, isSuperCreator),
        ];

        if (isHomepage(requestPath)) {
            additionalMenus = [
                getOrganizeMenu(organizeUrls, requestPath),
                ...additionalMenus,
            ];
        }

        return (
            <BaseGlobalHeader
                logoInfo={getLogoInfo(serverUrl, requestPath)}
                searchInfo={getSearchInfo(
                    serverUrl,
                    searchQuery,
                    searchSuggestions,
                    hideSearchBar,
                )}
                quickLinks={getBrowseEventsQuickLinks(
                    serverUrl,
                    ebDomain,
                    requestPath,
                    this.props.showReportBugLinkInHeader,
                )}
                userMenuInfo={getUserMenuInfo({
                    serverUrl,
                    adminServerUrl,
                    logoutUrl,
                    requestPath,
                    user,
                    isManageOverviewActive,
                    showBrowseEvents: hideSearchBar,
                    featureFlags,
                })}
                additionalMenus={additionalMenus}
                nonUserQuickLinks={getNonUserQuickLinks(
                    requestPath,
                    isAuthenticated,
                    loginUrl,
                )}
                callToActionInfo={getCreateEventCTA(
                    serverUrl,
                    ebDomain,
                    requestPath,
                    isAuthenticated,
                    canCreateEvents,
                )}
                onSearchChange={this._handleSearchChange}
                onSearchSelect={onSearchSelect}
                onSearchFocus={onSearchFocus}
                onSearchSubmit={this._handleSearchSubmit}
            />
        );
    }
}
